/**
 * Meta Pixel / CAPI 追踪基座
 *
 * 职责（仅追踪逻辑，不影响任何页面功能与布局）：
 *   1. 读取第一方 _fbp / _fbc cookie
 *   2. 生成稳定 external_id（登录客户 ID 或匿名 ID）
 *   3. 提供 SHA-256 哈希工具（邮箱 / 手机号高级匹配）
 *   4. 生成唯一 event_id（前端 Pixel 与后端 CAPI 共用同一来源）
 *   5. 全局表单委托：预哈希 email / phone，并在提交时触发 Lead
 */
(function () {
  'use strict';

  // ---------- Cookie ----------
  /**
   * @param {string} name
   * @returns {string}
   */
  function getCookie(name) {
    try {
      var m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
      var raw = m && m[1] ? m[1] : '';
      return raw ? decodeURIComponent(raw) : '';
    } catch (e) {
      return '';
    }
  } 

  // ---------- external_id ----------
  function getExternalId() {
    try {
      var id = '';
      // 登录客户：优先使用 Shopify customer.id（由 theme.liquid 注入 window.__meta_customer_id）
      if (window.__meta_customer_id) {
        id = String(window.__meta_customer_id);
      }
      if (!id) {
        id = localStorage.getItem('__meta_external_id') || '';
        if (!id) {
          id = 'anon_' + (window.crypto && crypto.randomUUID
            ? crypto.randomUUID()
            : Date.now().toString(36) + Math.random().toString(36).slice(2));
          localStorage.setItem('__meta_external_id', id);
        }
      }
      return id;
    } catch (e) {
      return '';
    }
  }

  // ---------- 用户匹配数据（em/ph 哈希） ----------
  /**
   * 读取已缓存的 email / phone 哈希，供各事件注入用户匹配数据
   * @returns {Record<string, string>}
   */
  function getHashedUserData() {
    var data = /** @type {Record<string, string>} */ ({});
    try {
      var em = localStorage.getItem('__meta_em') || '';
      var ph = localStorage.getItem('__meta_ph') || '';
      if (em) data.em = em;
      if (ph) data.ph = ph;
    } catch (e) { /* 忽略 */ }
    return data;
  }

  // ---------- 货币 ----------
  /**
   * 动态获取站点货币，取不到回退 USD
   * @returns {string}
   */
  function getCurrency() {
    try {
      if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
        return window.Shopify.currency.active;
      }
    } catch (e) { /* 忽略 */ }
    return 'USD';
  }

  // ---------- 规范化 ----------
  // 邮箱：去首尾空白 + 转小写（Meta 文档要求）
  /**
   * @param {string} value
   * @returns {string}
   */
  function normalizeEmail(value) {
    return String(value == null ? '' : value).trim().toLowerCase();
  }

  // 手机号：仅保留数字；美区号码（10 位）自动补美国区号 1（Meta 文档要求）
  /**
   * @param {string} value
   * @returns {string}
   */
  function normalizePhone(value) {
    var digits = String(value == null ? '' : value).replace(/\D/g, '');
    // 10 位纯数字视为美国号码，自动补区号 1；其他长度原样返回
    if (digits.length === 10) {
      digits = '1' + digits;
    }
    return digits;
  }

  // ---------- SHA-256 ----------
  /**
   * @param {string} value
   * @param {string} [type]
   * @returns {Promise<string>}
   */
  function sha256Hex(value, type) {
    try {
      if (!value) return Promise.resolve('');
      if (!(window.crypto && crypto.subtle && typeof TextEncoder !== 'undefined')) {
        return Promise.resolve('');
      }
      var normalized;
      if (type === 'ph') {
        normalized = normalizePhone(value);
      } else {
        normalized = normalizeEmail(value);
      }
      if (!normalized) return Promise.resolve('');
      var data = new TextEncoder().encode(normalized);
      return crypto.subtle.digest('SHA-256', data).then(function (buf) {
        return Array.prototype.map.call(new Uint8Array(buf), function (b) {
          return b.toString(16).padStart(2, '0');
        }).join('');
      });
    } catch (e) {
      return Promise.resolve('');
    }
  }

  // ---------- event_id ----------
  function newEventId() {
    try {
      if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    } catch (e) { /* 忽略，走降级方案 */ }
    return Date.now().toString(36) + '-' + Math.random().toString(36).slice(2);
  }

  // ---------- 表单捕获：预哈希 + Lead ----------
  /**
   * @param {string} storageKey
   * @param {string} hash
   */
  function cacheHash(storageKey, hash) {
    if (!hash) return;
    try { localStorage.setItem(storageKey, hash); } catch (e) { /* 忽略 */ }
  }

  /**
   * @param {HTMLInputElement} input
   */
  function bindInput(input) {
    if (!input || input.dataset.__metaBound) return;
    var isEmail = input.type === 'email' || input.name === 'contact[email]';
    var isPhone = input.type === 'tel' || input.name === 'contact[phone]';
    if (!isEmail && !isPhone) return;
    input.dataset.__metaBound = '1';
    var storageKey = isEmail ? '__meta_em' : '__meta_ph';
    var type = isEmail ? 'em' : 'ph';
    var hash = function () {
      var v = input.value;
      if (!v) return;
      sha256Hex(v, type).then(function (h) { cacheHash(storageKey, h); });
    };
    // 在用户填完邮箱/手机号的第一时间（change/blur）就完成 SHA-256 并缓存，
    // 保证表单跳转前哈希已就绪，同时供后端 CAPI 复用。
    input.addEventListener('change', hash);
    input.addEventListener('blur', hash);
  }

  var EMAIL_SELECTOR = 'input[type="email"], input[name="contact[email]"], input[type="tel"], input[name="contact[phone]"]';

  /**
   * @param {Document | Element} [root]
   */
  function scanForms(root) {
    var scope = root || document;
    if (!scope.querySelectorAll) return;
    var list = scope.querySelectorAll(EMAIL_SELECTOR);
    for (var i = 0; i < list.length; i++) bindInput(/** @type {HTMLInputElement} */ (list[i]));
  }

  // 动态 DOM（view transitions / 抽屉 / quick-add）下的新增字段
  if (typeof MutationObserver !== 'undefined') {
    var mo = new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var record = muts[i];
        if (!record) continue;
        var added = record.addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (!node || node.nodeType !== 1) continue;
          var el = /** @type {Element} */ (node);
          if (el.matches && el.matches(EMAIL_SELECTOR)) {
            bindInput(/** @type {HTMLInputElement} */ (el));
          } else {
            scanForms(el);
          }
        }
      }
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
  }

  // 提交委托（capture 阶段）：跳转前触发 Lead，等待异步哈希就绪后再上报（修复极速回车竞态）
  document.addEventListener('submit', function (e) {
    var form = /** @type {HTMLFormElement | null} */ (e.target);
    if (!form || form.nodeType !== 1 || form.tagName !== 'FORM') return;

    var em = '';
    var ph = '';
    try { em = localStorage.getItem('__meta_em') || ''; } catch (err) { /* 忽略 */ }
    try { ph = localStorage.getItem('__meta_ph') || ''; } catch (err) { /* 忽略 */ }

    var emailInput = /** @type {HTMLInputElement | null} */ (
      form.querySelector('input[type="email"], input[name="contact[email]"]')
    );
    var phoneInput = /** @type {HTMLInputElement | null} */ (
      form.querySelector('input[type="tel"], input[name="contact[phone]"]')
    );

    // 缓存为空但输入框有值：异步补齐（结果写入 localStorage，供后端 CAPI 复用）
    var pending = [];
    if (!em && emailInput && emailInput.value) {
      pending.push(sha256Hex(emailInput.value, 'em').then(function (h) { cacheHash('__meta_em', h); }));
    }
    if (!ph && phoneInput && phoneInput.value) {
      pending.push(sha256Hex(phoneInput.value, 'ph').then(function (h) { cacheHash('__meta_ph', h); }));
    }

    function fireLead() {
      var finalEm = em;
      var finalPh = ph;
      if (!finalEm) { try { finalEm = localStorage.getItem('__meta_em') || ''; } catch (err) { /* 忽略 */ } }
      if (!finalPh) { try { finalPh = localStorage.getItem('__meta_ph') || ''; } catch (err) { /* 忽略 */ } }
      if (!finalEm && !finalPh) return;

      var data = /** @type {Record<string, string>} */ ({ external_id: getExternalId() });
      if (finalEm) data.em = finalEm;
      if (finalPh) data.ph = finalPh;
      try {
        if (typeof fbq === 'function') {
          var eventId = newEventId();
          window.__youwei_lastLeadEventId = eventId; // 持久化，供后端 CAPI 复用同一 event_id
          fbq('track', 'Lead', data, { eventID: eventId });
        }
      } catch (err) { /* 忽略 */ }
    }

    if (pending.length > 0) {
      // 等待异步哈希就绪后再上报，避免极速回车时漏发
      Promise.all(pending).then(fireLead);
    } else {
      fireLead();
    }
  }, true);

  // 初始扫描
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scanForms(document); });
  } else {
    scanForms(document);
  }

  window.__metaTracking = {
    getCookie: getCookie,
    getExternalId: getExternalId,
    getHashedUserData: getHashedUserData,
    getCurrency: getCurrency,
    sha256: sha256Hex,
    newEventId: newEventId,
    fbp: getCookie('_fbp'),
    fbc: getCookie('_fbc'),
  };
})();
